import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import Upload from 'hooks/useDirectUpload/Upload';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import {
  collectedFacilities,
  CollectedName,
  collectedNameValue,
  FacilityOption,
  pickedFacilityIds,
  PORTAL_COLLECT_FIELDS,
  ReferralFieldDef,
  sourceSystemLabel,
  VALUE_NAMES,
} from './constants';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  // Values read off the portal elsewhere (the extension's manual HUD), landing in the fields
  // for the person to review — never overwriting what they have already entered here.
  prefill?: Record<string, unknown> | null;
}

// The collect fields whose entry state is one plain string. The other datatypes own their own
// state — a Date, a name in the shape the person chose, a column of facility picks.
const TEXT_COLLECT_FIELDS = PORTAL_COLLECT_FIELDS.filter((field) => field.type === 'text' || field.type === 'select');

// Facilities lead the form on their own, above the optional details: a referral goes somewhere,
// and the rows are taller than one line. Falling back to the label rather than asserting the
// field is there keeps a reordered declaration from blanking the heading of a section this form
// renders by hand anyway.
const FACILITY_LABEL = PORTAL_COLLECT_FIELDS.find((field) => field.type === 'facility')?.label ?? 'Facilities';
const DETAIL_COLLECT_FIELDS = PORTAL_COLLECT_FIELDS.filter((field) => field.type !== 'facility');

// Where to go get the information — the whole instruction the kickoff carried. A URL
// pointer renders as a link (and later becomes the browser-extension hand-off).
function SourceContext({ workflow }: { workflow: Workflow }) {
  const system = workflow.valueFor('source_system')?.value;
  const pointer = workflow.valueFor('retrieval_pointer')?.value;
  if (!system && !pointer) return null;

  return (
    <SourceLine data-testid='retrieve-source-context'>
      {system && <BodySmallBold>{sourceSystemLabel(system)}</BodySmallBold>}
      {pointer &&
        (/^https?:\/\//.test(pointer) ? (
          <PointerLink href={pointer} target='_blank' rel='noreferrer'>
            {pointer}
          </PointerLink>
        ) : (
          <Pointer>{pointer}</Pointer>
        ))}
    </SourceLine>
  );
}

// Document-first manual fallback for the source-portal pull: the person saves the
// referral documents out of the portal and uploads them here — the AI actions read the
// demographics from the documents. The collect-fields are the optional shortcut: any
// value typed here is committed at completion and outranks the downstream extraction,
// so what a person read off the portal is never overwritten by the AI.
export default function RetrievePortalForm({ action, workflow, onComplete, isCompleting, prefill }: Props) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_COLLECT_FIELDS.map((field) => [field.name, '']))
  );
  const [dob, setDob] = useState<Date | null>(null);
  // The collected name, in whichever shape the portal put in front of the person: the
  // first/last pair by default, or one name field when that is all the portal shows.
  const [nameParts, setNameParts] = useState({ first: '', last: '' });
  const [fullName, setFullName] = useState('');
  const [singleName, setSingleName] = useState(false);
  // Set by any of the name inputs, so a late HUD prefill knows to leave the name alone.
  const nameTouched = useRef(false);
  // The facilities this referral names — one row each, every one a pick, so what this form
  // collects is always an Olio facility and never something the mapping step has to resolve.
  const [facilities, setFacilities] = useState<(FacilityOption | null)[]>([null]);

  // The same facility source the referral modal picks from.
  const { facilities: allowedFacilities } = useAllowedFacilities();
  const facilityOptions: FacilityOption[] = useMemo(
    () => allowedFacilities.map((group) => ({ id: group.id, name: group.name })),
    [allowedFacilities]
  );

  // A facility already picked in another row is off the menu — picking it twice would address
  // the same building twice and collapse to one entry anyway.
  const optionsFor = (row: FacilityOption | null) =>
    facilityOptions.filter((option) => option.id === row?.id || !facilities.some((pick) => pick?.id === option.id));

  // Prefill fills only fields the person hasn't touched — their own typing wins.
  // Applied in two independently-guarded passes because only ONE of them has a dependency:
  // resolving a facility id needs the groups query, and everything else does not. Gating the
  // whole prefill on the options list would mean a person with no allowed facilities — or a
  // slow or failed query — silently loses the name, DOB and text the HUD sent.
  // Each ref ensures its pass runs at most once, since facilityOptions gains a new identity
  // when the query resolves and re-running would clobber anything typed since.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!prefill || prefilled.current) return;
    prefilled.current = true;
    const stringAt = (name: string) => {
      const value = prefill[name];
      return typeof value === 'string' ? value : '';
    };
    setText((prev) => Object.fromEntries(Object.entries(prev).map(([name, value]) => [name, value || stringAt(name)])));
    if (stringAt(VALUE_NAMES.PATIENT_DOB)) {
      setDob((prev) => prev ?? parseDate(stringAt(VALUE_NAMES.PATIENT_DOB)) ?? null);
    }
    // A name being typed wins over the prefill — including the mode switch. A {full} send that
    // flipped the form to single-name would hide the typed pair AND drop it on submit, which is
    // the one way prefill could destroy work rather than skip it, so one touch guards all three
    // pieces of name state at once.
    // A bare string is read as the single shape: a surface that predates the name datatype sends
    // the one name it had, and showing it is what keeps those sends from being dropped.
    const sent = prefill[VALUE_NAMES.PATIENT_NAME];
    const prefillName: CollectedName | null =
      typeof sent === 'string' ? { full: sent } : sent && typeof sent === 'object' ? (sent as CollectedName) : null;
    if (prefillName && !nameTouched.current) {
      if (prefillName.full) {
        setSingleName(true);
        setFullName(prefillName.full);
      } else {
        setNameParts({ first: prefillName.first ?? '', last: prefillName.last ?? '' });
      }
    }
  }, [prefill]);

  // The HUD picks from the same facility list, so its entries arrive as ids to look up — which
  // is what this pass waits for. A person with no facilities has nothing to resolve against.
  const facilitiesPrefilled = useRef(false);
  useEffect(() => {
    if (!prefill || facilitiesPrefilled.current || facilityOptions.length === 0) return;
    facilitiesPrefilled.current = true;
    const prefillFacilities = pickedFacilityIds(prefill[VALUE_NAMES.FACILITIES])
      .map((id) => facilityOptions.find((option) => option.id === id))
      .filter((option): option is FacilityOption => !!option);
    if (prefillFacilities.length > 0) setFacilities((prev) => (prev.some(Boolean) ? prev : prefillFacilities));
  }, [prefill, facilityOptions]);

  const readyUploads = uploads.filter((upload) => upload.signedId);
  const ready = readyUploads.length > 0 && !isUploading && !isCompleting;

  const typedValues = (): WorkflowValuesInput => {
    const values: WorkflowValuesInput = {};
    TEXT_COLLECT_FIELDS.forEach((field) => {
      const value = text[field.name].trim();
      if (value) values[field.name] = value;
    });
    const dashDob = simpleDashDate(dob);
    if (dashDob) values[VALUE_NAMES.PATIENT_DOB] = dashDob;
    const collectedName = collectedNameValue(singleName ? { full: fullName } : nameParts);
    if (collectedName) values[VALUE_NAMES.PATIENT_NAME] = collectedName;
    return values;
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    onComplete(action.id, {
      attachments: readyUploads.map((upload) => ({ signedId: upload.signedId!, filename: upload.file.name })),
      [VALUE_NAMES.FACILITIES]: collectedFacilities(facilities),
      ...typedValues(),
    });
  }

  const setFacilityRow = (index: number, row: FacilityOption | null) =>
    setFacilities((prev) => prev.map((current, i) => (i === index ? row : current)));

  const removeFacility = (index: number) =>
    setFacilities((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [null]));

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        <Optional> (optional)</Optional>
      </FieldLabel>
      {field.type === 'date' && <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />}
      {field.type === 'select' && (
        <div data-testid={`retrieve-field-${field.name}`}>
          <Select
            inputId={`retrieve-field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'name' && (
        <div data-testid={`retrieve-field-${field.name}`}>
          {singleName ? (
            <Input
              value={fullName}
              onChange={(e) => {
                nameTouched.current = true;
                setFullName(e.target.value);
              }}
              placeholder='Name as the portal shows it'
              data-testid={`retrieve-field-${field.name}-full`}
            />
          ) : (
            <NameRow>
              <Input
                value={nameParts.first}
                onChange={(e) => {
                  nameTouched.current = true;
                  setNameParts((prev) => ({ ...prev, first: e.target.value }));
                }}
                placeholder='First'
                data-testid={`retrieve-field-${field.name}-first`}
              />
              <Input
                value={nameParts.last}
                onChange={(e) => {
                  nameTouched.current = true;
                  setNameParts((prev) => ({ ...prev, last: e.target.value }));
                }}
                placeholder='Last'
                data-testid={`retrieve-field-${field.name}-last`}
              />
            </NameRow>
          )}
          <AddLink type='button' onClick={() => setSingleName((prev) => !prev)}>
            {singleName ? 'Enter first and last separately' : 'The portal shows one name field'}
          </AddLink>
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          data-testid={`retrieve-field-${field.name}`}
        />
      )}
    </Field>
  );

  return (
    <Form onSubmit={submit} data-testid='retrieve-portal-form'>
      <SourceContext workflow={workflow} />
      <BodySmall>
        Save the referral documents from the source portal (download or print to PDF), then upload them here. The
        patient&apos;s details are read from the documents.
      </BodySmall>
      <UploadZone data-testid='retrieve-documents'>
        <FileInputComponent onChange={setUploads} onUploading={setIsUploading} uploadList='inside' />
      </UploadZone>
      <Field data-testid='retrieve-facilities'>
        <FieldLabel>{FACILITY_LABEL}</FieldLabel>
        {facilities.map((row, index) => (
          // Index keys are safe here: rows are positional (no identity beyond their slot).
          <FacilityRowLine key={index}>
            <SelectCell data-testid={`retrieve-facility-${index}`}>
              <Select<FacilityOption>
                inputId={`retrieve-facility-${index}`}
                placeholder='Select a facility'
                options={optionsFor(row)}
                value={row}
                onChange={(pick) => setFacilityRow(index, pick)}
                menuPortalTarget={document.body}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
              />
            </SelectCell>
            <RowButton
              type='button'
              aria-label='Remove facility'
              onClick={() => removeFacility(index)}
              data-testid={`retrieve-facility-remove-${index}`}>
              ×
            </RowButton>
          </FacilityRowLine>
        ))}
        <AddLink
          type='button'
          onClick={() => setFacilities((prev) => [...prev, null])}
          data-testid='retrieve-facility-add'>
          + Add facility
        </AddLink>
      </Field>
      <BodySmall>Anything you already know from the portal can be entered directly:</BodySmall>
      <FieldGrid data-testid='retrieve-collect-fields'>{DETAIL_COLLECT_FIELDS.map(renderField)}</FieldGrid>
      <div>
        <Button type='submit' size='small' disabled={!ready} data-testid='retrieve-submit'>
          Save retrieved documents
        </Button>
      </div>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadZone = styled.div`
  margin-top: 4px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px 12px;
`;

const NameRow = styled.div`
  display: flex;
  gap: 8px;

  > * {
    flex: 1;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Optional = styled.span`
  color: ${colors.black50};
  font-weight: 400;
`;

const FacilityRowLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 320px;
`;

const SelectCell = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${colors.black50};
  font-size: 16px;
  line-height: 1;
`;

const AddLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-size: 12px;
  text-decoration: underline;
`;

const SourceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.black5};
`;

const Pointer = styled(BodySmall)`
  color: ${colors.black50};
  word-break: break-all;
`;

const PointerLink = styled.a`
  font-size: 12px;
  color: ${colors.blue100};
  word-break: break-all;
`;
